// G-UPLOAD-01 — tenant asset upload via the enterprise presign 2-step (replaces the legacy
// /uploadFile/uploadFile byte-proxy, which tunnelled file bytes through the API to S3).
//
// Upload:  POST /invoices/upload-url { name, type } -> { url (presigned PUT), storageUri (key) }
//          PUT bytes to the presigned URL (no auth header — the PAR is the auth)
//          -> the stable value to PERSIST is the storage KEY (storageUri), never the URL.
// Display: resolveAssetUrl(value) turns a stored key into a short-lived presigned GET URL via
//          POST /documents/access (1h PAR on OCI). Keys must be resolved per render/session;
//          full http(s)/data: values (legacy v4 S3/CloudFront URLs) pass through unchanged so
//          pre-migration rows keep rendering.
//
// Consumers: Companyinfo (company logo), SupplierTablesecond (supplier document upload — its
// view/download path already resolves filePath through /documents/access).

import Axios from "axios";
import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { PRE_SIGNED_URL, ACCESS_S3_FILE } from "../common/url";

// True for values that are already directly renderable (legacy absolute URLs, data/blob URIs).
function isDirectUrl(value: string): boolean {
  return /^(https?:|data:|blob:)/i.test(value);
}

// Presign + PUT the bytes; returns the storage key to persist.
// [UAT-PROFILE] presignUrl defaults to the invoice-pathed route (company logo / supplier docs,
// the original consumers); pass ASSET_PRE_SIGNED_URL for tenant assets (profile photos) so the
// key lands under tenants/<tenant>/assets/... instead of the invoice inbox.
export async function uploadAsset(
  file: File | Blob,
  fileName: string,
  presignUrl: string = PRE_SIGNED_URL
): Promise<{ storageUri: string }> {
  const contentType = (file as File).type || "application/octet-stream";
  const presign = await API({
    method: "POST",
    url: presignUrl,
    headers: authHeader(),
    data: { name: fileName, type: contentType },
  });
  const { status, url, storageUri } = presign.data as {
    status: string;
    url: string;
    storageUri: string;
  };
  if (status !== "Success" || !url || !storageUri) {
    throw new Error("Could not presign upload");
  }
  // Plain axios (NOT the API instance): the presigned PUT must not carry the app auth header.
  await Axios.put(url, file, { headers: { "Content-Type": contentType } });
  return { storageUri };
}

// [UAT-PROFILE] Session-cached variant of resolveAssetUrl for repeated keys (chat avatars
// re-render the same poster keys on every comment poll — resolve each key once per session).
const resolvedKeyCache = new Map<string, Promise<string>>();
export function resolveAssetUrlCached(value: string | null | undefined): Promise<string> {
  if (!value) return Promise.resolve("");
  if (isDirectUrl(value)) return Promise.resolve(value);
  let cached = resolvedKeyCache.get(value);
  if (!cached) {
    cached = resolveAssetUrl(value).then((url) => {
      // Don't cache failures — a transient error shouldn't blank the avatar for the session.
      if (!url) resolvedKeyCache.delete(value);
      return url;
    });
    resolvedKeyCache.set(value, cached);
  }
  return cached;
}

// Resolve a stored asset value into something an <img src>/<a href> can use.
// Degrades cleanly: returns "" on any failure (no uncaught, no broken request loop).
export async function resolveAssetUrl(value: string | null | undefined): Promise<string> {
  if (!value) return "";
  if (isDirectUrl(value)) return value;
  try {
    const response = await API({
      method: "POST",
      url: ACCESS_S3_FILE,
      headers: authHeader(),
      data: { filePath: value },
    });
    const { url } = response.data as { url?: string };
    return url ?? "";
  } catch {
    return "";
  }
}
